import type { FunctionComponent, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { any, chain } from 'underscore';
import { useDebouncedCallback } from 'use-debounce';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { fireConfetti, shouldFireConfetti } from '@trello/confetti';
import type { EmojiMartEmoji } from '@trello/emoji';
import { getNetworkError } from '@trello/graphql-error-handling';
import { intl } from '@trello/i18n';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Popover, usePopover } from '@trello/nachos/popover';
import { useSharedState } from '@trello/shared-state';

import type { LiveUpdate } from 'app/scripts/init/live-updater';
import { useSocketUpdate } from 'app/scripts/init/useSocketUpdate';
import { EmojiPickerPopover } from 'app/src/components/EmojiPickerPopover';
import type { NotificationType } from 'app/src/components/NotificationsMenu';
import { ReactionAnalyticsContextProvider } from 'app/src/components/ReactionAnalyticsContext';
import { useAddReactionMutation } from './AddReactionMutation.generated';
import { addReactionToSharedState } from './addReactionToSharedState';
import { applySocketReaction } from './applySocketReaction';
import { clearPendingReactionUpdates } from './clearPendingReactionUpdates';
import { convertReactionToClientFormat } from './convertReactionToClientFormat';
import { convertSkinToServerFormat } from './convertSkinToServerFormat';
import { createReaction } from './createReaction';
import { deleteReactionFromSharedState } from './deleteReactionFromSharedState';
import { useDeleteReactionMutation } from './DeleteReactionMutation.generated';
import { getReactionAnalyticsContainersByActionId } from './getReactionAnalyticsContainersByActionId';
import { Reaction } from './Reaction';
import { ReactionAddButton } from './ReactionAddButton';
import type { ReactionModel } from './Reactions.types';
import { reactionsState } from './reactionsState';
import { ReactionTooltip } from './ReactionTooltip';
import { registerPendingReactionUpdate } from './registerPendingReactionUpdate';
import { updateReactionInSharedState } from './updateReactionInSharedState';

import styles from './ReactionPiles.less';

const EMOJI_POPOVER_WIDTH = 408;

/*
These limits live on the server: app/data/limits.js
Limits are attached to actions but not exposed to notifications
https://trello.atlassian.net/browse/WLRS-122 Add reaction limits to notifications
This will help to show the red box immediately rather than making the api request
*/
const ReactionLimits = {
  perAction: 1000,
  uniquePerAction: 17,
} as const;

interface ReactionPilesProps {
  idAction?: string;
  idNotification?: string;
  notificationType?: NotificationType;
  canReact: boolean;
  isCardBack?: boolean;
}

export const ReactionPiles: FunctionComponent<ReactionPilesProps> = ({
  idAction,
  idNotification,
  notificationType,
  canReact,
  isCardBack = false,
}) => {
  const memberId = useMemberId();
  const { toggle, hide, popoverProps, triggerRef } =
    usePopover<HTMLDivElement>();
  const [addReactionMutation] = useAddReactionMutation();
  const [deleteReactionMutation] = useDeleteReactionMutation();
  const [reactionsSharedState] = useSharedState(reactionsState);

  const handleReaction = useCallback((update: LiveUpdate['Reaction']) => {
    applySocketReaction(update.delta);
  }, []);

  useSocketUpdate<'Reaction'>({
    modelName: 'Reaction',
    onMessage: handleReaction,
  });

  const analyticsContainers = useMemo(
    () => getReactionAnalyticsContainersByActionId(idAction ?? ''),
    [idAction],
  );
  const reactions = useMemo(() => {
    return (
      (idAction !== undefined
        ? reactionsSharedState.reactions[idAction]
        : []) || []
    );
  }, [idAction, reactionsSharedState.reactions]);

  const groupedReactions = useMemo(() => {
    return chain(reactions)
      .groupBy((reaction) => reaction.emoji.shortcodes)
      .map((grouped) => ({
        reaction: grouped[0],
        count: grouped.length,
        tooltip: (
          <ReactionTooltip
            members={grouped.map((reaction) => reaction.member)}
            shortcodes={grouped[0].emoji.shortcodes}
            myId={memberId ?? ''}
          />
        ),
        isMyReaction: any(
          grouped,
          (reaction) => reaction.idMember === memberId,
        ),
      }))
      .value();
  }, [memberId, reactions]);

  const isEmpty = groupedReactions.length === 0;

  const confettiCheck = useCallback(
    (emoji: EmojiMartEmoji, e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
      if (
        shouldFireConfetti(emoji.native) &&
        !any(groupedReactions, (groupedReaction) => {
          return (
            groupedReaction.isMyReaction &&
            groupedReaction.reaction.emoji.id === emoji.id.toUpperCase()
          );
        })
      ) {
        fireConfetti({
          x: e.pageX / window.innerWidth,
          y: e.pageY / window.innerHeight,
        });
      }
    },
    [groupedReactions],
  );

  const addReaction = useCallback(
    async (tempReaction: ReactionModel) => {
      try {
        const { data: addReactionData } = await addReactionMutation({
          variables: {
            idAction: tempReaction.idModel,
            skinVariation:
              convertSkinToServerFormat(tempReaction.emoji.skin) || undefined,
            unified: tempReaction.emoji.unified.toUpperCase(),
          },
        });
        if (addReactionData?.addReaction) {
          Analytics.sendTrackEvent({
            action: 'added',
            actionSubject: 'reaction',
            source: 'reactionSelectorInlineDialog',
            attributes: {
              reactionId: addReactionData.addReaction.id,
              commentId: addReactionData.addReaction.idModel,
              addedTo: 'notificationComment',
            },
            containers: formatContainers(analyticsContainers),
          });

          const clientFormat = convertReactionToClientFormat(
            addReactionData.addReaction,
          );
          updateReactionInSharedState(clientFormat);
        }
      } catch (error) {
        const networkError = getNetworkError(error);
        deleteReactionFromSharedState(tempReaction);
        showFlag({
          id: 'reactions',
          appearance: 'error',
          title:
            networkError?.code === 'ACTION_TOO_MANY_UNIQUE_REACTIONS'
              ? intl.formatMessage(
                  {
                    id: 'templates.reactions.unique-reaction-limit-exceeded',
                    defaultMessage:
                      "We {heart} emoji reactions too, but there's a limit of {number} emoji per comment.",
                    description:
                      'The user has reached the limit of unique reactions',
                  },
                  {
                    heart: '❤',
                    number: ReactionLimits.uniquePerAction.toString(),
                  },
                )
              : intl.formatMessage(
                  {
                    id: 'templates.reactions.total-reaction-limit-exceeded',
                    defaultMessage:
                      "We {heart} emoji reactions too, but there's a limit of {number} total reactions per comment.",
                    description:
                      'The user has reached the limit of total reactions',
                  },
                  {
                    heart: '❤',
                    number: ReactionLimits.perAction.toString(),
                  },
                ),
          isAutoDismiss: true,
          msTimeout: 3000,
        });
      }
    },
    [addReactionMutation, analyticsContainers],
  );

  const deleteReaction = useCallback(
    async (reaction: ReactionModel) => {
      try {
        const { data: deleteReactionData } = await deleteReactionMutation({
          variables: { idAction: reaction.idModel, reactionId: reaction.id },
        });
        if (deleteReactionData) {
          Analytics.sendUIEvent({
            action: 'removed',
            actionSubject: 'reaction',
            source: 'reactionSelectorInlineDialog',
            attributes: {
              reactionId: reaction.id,
              commentId: reaction.idModel,
              removedFrom: 'notificationComment',
            },
            containers: formatContainers(analyticsContainers),
          });
        }
      } catch (e) {
        // Ignore removal failures. They're usually 404s, meaning the reaction
        // already got removed by a previous request (possibly from another tab).
      }
    },
    [analyticsContainers, deleteReactionMutation],
  );

  const persistUpdates = useCallback(async () => {
    const pendingUpdatesCopy = Object.values(
      reactionsSharedState.pendingUpdates,
    );
    clearPendingReactionUpdates();

    await Promise.all(
      pendingUpdatesCopy.map(async ({ reaction, type, ...rest }) => {
        if (type === 'add') {
          await addReaction(reaction);
        } else if (type === 'remove') {
          if (!reaction.id) {
            registerPendingReactionUpdate({ reaction, type, ...rest });
            return;
          }
          await deleteReaction(reaction);
        }
      }),
    );
  }, [addReaction, deleteReaction, reactionsSharedState.pendingUpdates]);

  const debouncedPersistUpdates = useDebouncedCallback(persistUpdates, 1000);

  const toggleReaction = useCallback(
    async (emoji: EmojiMartEmoji) => {
      const myReaction = reactions.find(
        (actionReaction) =>
          actionReaction.idEmoji === emoji.unified.toUpperCase() &&
          actionReaction.idMember === memberId,
      );
      if (myReaction) {
        deleteReactionFromSharedState(myReaction);
        registerPendingReactionUpdate({
          idAction: idAction || '',
          reaction: myReaction,
          type: 'remove',
        });
        await debouncedPersistUpdates();
      } else {
        const newReaction = createReaction(emoji, idAction || '');
        addReactionToSharedState(newReaction);
        registerPendingReactionUpdate({
          idAction: idAction || '',
          reaction: newReaction,
          type: 'add',
        });
        await debouncedPersistUpdates();
      }
    },
    [debouncedPersistUpdates, idAction, memberId, reactions],
  );

  const onSelectEmojiMartEmoji = useCallback(
    async (
      emoji: EmojiMartEmoji,
      e: ReactMouseEvent<HTMLElement, MouseEvent>,
    ) => {
      confettiCheck(emoji, e);
      hide();
      await toggleReaction(emoji);
    },
    [confettiCheck, hide, toggleReaction],
  );

  const onReactionClick = useCallback(
    async (
      reactionModel: ReactionModel,
      e: ReactMouseEvent<HTMLElement, MouseEvent>,
    ) => {
      confettiCheck(reactionModel.emoji, e);
      await toggleReaction(reactionModel.emoji);
    },
    [confettiCheck, toggleReaction],
  );

  return (
    <ReactionAnalyticsContextProvider
      analyticsContainers={analyticsContainers}
      idAction={idAction}
      idNotification={idNotification}
      notificationType={notificationType}
    >
      <div
        className={classNames(styles.reactionPiles, {
          [styles.empty]: isEmpty,
          [styles.cardBack]: isCardBack,
        })}
      >
        {groupedReactions.map(({ reaction, count, isMyReaction, tooltip }) => (
          <Reaction
            key={reaction.idEmoji}
            reaction={reaction}
            count={count}
            isMyReaction={isMyReaction}
            isDisabled={!canReact}
            tooltip={tooltip}
            onClick={onReactionClick}
          />
        ))}
        {(canReact || !isCardBack) && (
          <div ref={triggerRef}>
            <ReactionAddButton
              isSmall={isEmpty}
              isDisabled={!canReact}
              onClick={toggle}
              isActive={popoverProps.isVisible}
              isCardBack={isCardBack}
            />
          </div>
        )}
        <Popover
          {...popoverProps}
          noHorizontalPadding
          noVerticalPadding
          dangerous_width={EMOJI_POPOVER_WIDTH}
        >
          <EmojiPickerPopover onSelectEmoji={onSelectEmojiMartEmoji} />
        </Popover>
      </div>
    </ReactionAnalyticsContextProvider>
  );
};
